import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { ArrowLeft, Loader2, PackageCheck, Pencil } from "lucide-react";
import { toast } from "sonner";
import { ConfirmDialog } from "@/components/shared/confirm-dialog";
import {
  CollapsibleBlock,
  DocumentHeader,
  DocumentPage,
  DocumentStickyBar,
  useDocumentSaveShortcut,
} from "@/components/shared/document-shell";
import { EmptyState } from "@/components/shared/empty-state";
import { EntityPicker } from "@/components/shared/entity-picker";
import { ErrorBoundary } from "@/components/shared/error-boundary";
import { Field, FieldGrid } from "@/components/shared/form";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useStockLocations } from "@/features/locations/api";
import { usePurchaseOrder, useUpdatePurchaseOrderStatus } from "@/features/purchase/api";
import { canReceive } from "@/features/purchase/constants";
import {
  openReceiveLines,
  remainingWeight,
  useReceiveGoods,
  type ReceiveLineInput,
} from "@/features/purchase/receive-api";
import { ReceiveLineDialog } from "@/features/purchase/receive-line-dialog";
import { useCompany } from "@/features/settings/api";
import { useVendors } from "@/features/vendors/api";

export function GoodsReceiptPage({ poId }: { poId: string }) {
  return (
    <ErrorBoundary title="Goods receipt crashed">
      <GoodsReceiptInner poId={poId} />
    </ErrorBoundary>
  );
}

function GoodsReceiptInner({ poId }: { poId: string }) {
  const navigate = useNavigate();
  const { data: po, isLoading, error } = usePurchaseOrder(poId);
  const { data: locations = [] } = useStockLocations();
  const { data: vendors = [] } = useVendors();
  const receive = useReceiveGoods();
  const updateStatus = useUpdatePurchaseOrderStatus();

  const [notes, setNotes] = useState("");
  const [bulkLocationId, setBulkLocationId] = useState("");
  const [staged, setStaged] = useState<ReceiveLineInput[]>([]);
  const [editingLineId, setEditingLineId] = useState<string | null>(null);
  const [partialPrompt, setPartialPrompt] = useState<{
    receipt_no: string;
  } | null>(null);

  const activeLocations = useMemo(
    () => locations.filter((l) => l.is_active),
    [locations],
  );
  const { data: company } = useCompany();
  const defaultLocationId = useMemo(() => {
    if (
      company?.default_location_id &&
      activeLocations.some((l) => l.id === company.default_location_id)
    ) {
      return company.default_location_id;
    }
    const prefer = activeLocations.find(
      (l) =>
        /yard|shed|raw|coil/i.test(`${l.code} ${l.name}`) || l.location_type === "yard",
    );
    return prefer?.id ?? activeLocations[0]?.id ?? "";
  }, [activeLocations, company?.default_location_id]);

  const openLines = po ? openReceiveLines(po) : [];
  const vendor = vendors.find((v) => v.id === po?.vendor_id);

  useEffect(() => {
    if (defaultLocationId && !bulkLocationId) {
      setBulkLocationId(defaultLocationId);
    }
  }, [defaultLocationId, bulkLocationId]);

  const locationOptions = useMemo(
    () =>
      activeLocations.map((l) => ({
        id: l.id,
        label: `${l.code} — ${l.name}`,
        keywords: `${l.code} ${l.name} ${l.location_type}`,
        meta: l.location_type,
      })),
    [activeLocations],
  );

  const editingLine = openLines.find((l) => l.id === editingLineId) ?? null;

  const totalWeight = useMemo(
    () => staged.reduce((sum, s) => sum + (s.weight > 0 ? s.weight : s.quantity), 0),
    [staged],
  );

  const goToPo = useCallback(() => {
    void navigate({ to: "/purchase/orders/$poId", params: { poId } });
  }, [navigate, poId]);

  const postReceipt = useCallback(async () => {
    if (!staged.length) {
      toast.error("Add at least one line via the receive form");
      return;
    }
    try {
      const result = await receive.mutateAsync({
        purchaseOrderId: poId,
        lines: staged,
        notes,
      });
      setStaged([]);
      if (result.po_status === "partial") {
        setPartialPrompt({ receipt_no: result.receipt_no });
        return;
      }
      goToPo();
    } catch {
      /* mutation toast */
    }
  }, [staged, notes, poId, receive, goToPo]);

  useDocumentSaveShortcut(() => {
    void postReceipt();
  }, !receive.isPending && staged.length > 0 && !partialPrompt);

  if (isLoading) {
    return <p className="py-16 text-center text-sm text-muted-foreground">Loading…</p>;
  }

  if (error || !po) {
    return (
      <EmptyState
        title="Purchase order not found"
        description={(error as Error)?.message ?? "Unknown PO"}
        action={
          <Button asChild variant="outline" size="sm">
            <Link to="/purchase">Back to list</Link>
          </Button>
        }
      />
    );
  }

  if (!canReceive(po.status)) {
    return (
      <EmptyState
        title="Cannot receive against this PO"
        description={`Status is “${po.status}”. Receive is only available for confirmed or partial orders.`}
        action={
          <Button asChild variant="outline" size="sm">
            <Link to="/purchase/orders/$poId" params={{ poId }}>
              Back to PO
            </Link>
          </Button>
        }
      />
    );
  }

  if (!openLines.length) {
    return (
      <EmptyState
        title="Nothing left to receive"
        description="All lines are fully received."
        action={
          <Button asChild variant="outline" size="sm">
            <Link to="/purchase/orders/$poId" params={{ poId }}>
              Back to PO
            </Link>
          </Button>
        }
      />
    );
  }

  return (
    <DocumentPage>
      <DocumentHeader
        back={
          <Button asChild variant="ghost" size="icon-sm">
            <Link to="/purchase/orders/$poId" params={{ poId }}>
              <ArrowLeft />
            </Link>
          </Button>
        }
        title="Receive stock"
        subtitle={
          <>
            {po.po_number} · {po.vendor_code} · {po.vendor_name}
            {vendor?.batch_prefix ? ` · Batch ${vendor.batch_prefix}-YYMM-####` : ""}
            {" · Open line → enter actuals"}
          </>
        }
        actions={
          <>
            <Button asChild type="button" variant="outline" size="sm">
              <Link to="/purchase/orders/$poId" params={{ poId }}>
                Cancel
              </Link>
            </Button>
            <Button
              type="button"
              size="sm"
              disabled={receive.isPending || !staged.length}
              onClick={() => void postReceipt()}
            >
              {receive.isPending ? <Loader2 className="animate-spin" /> : <PackageCheck />}
              Post receipt
            </Button>
          </>
        }
      />

      <div className="space-y-2">
        <div className="flex flex-wrap items-end gap-2 border-b border-border/80 pb-2">
          <div className="min-w-[14rem] flex-1 space-y-0.5">
            <label className="field-label" htmlFor="grn-bulk-loc">
              Default location
            </label>
            <EntityPicker
              id="grn-bulk-loc"
              value={bulkLocationId}
              options={locationOptions}
              recentKey="locations"
              placeholder="Type location code…"
              onChange={setBulkLocationId}
            />
          </div>
        </div>

        <div className="overflow-hidden rounded-md border border-border">
          <table className="w-full text-xs">
            <thead className="bg-surface/80 text-2xs uppercase tracking-[0.04em] text-muted-foreground">
              <tr>
                <th className="px-2 py-1.5 text-left font-semibold">#</th>
                <th className="px-2 py-1.5 text-left font-semibold">Material</th>
                <th className="px-2 py-1.5 text-right font-semibold">Ordered</th>
                <th className="px-2 py-1.5 text-right font-semibold">Remaining</th>
                <th className="px-2 py-1.5 text-right font-semibold">Staged</th>
                <th className="px-2 py-1.5 text-right font-semibold" />
              </tr>
            </thead>
            <tbody>
              {openLines.map((line) => {
                const rem = remainingWeight(line);
                const stagedLine = staged.find((s) => s.purchase_order_line_id === line.id);
                return (
                  <tr key={line.id} className="border-t border-border/60">
                    <td className="px-2 py-2 tabular-nums text-muted-foreground">{line.line_no}</td>
                    <td className="px-2 py-2">
                      <div className="font-medium">{line.material_name || "—"}</div>
                      <div className="text-[10px] text-muted-foreground">
                        {[line.grade_name, line.shape_label].filter(Boolean).join(" · ") || "—"}
                      </div>
                    </td>
                    <td className="px-2 py-2 text-right tabular-nums text-muted-foreground">
                      {line.ordered_weight}
                      {line.uom_code ? ` ${line.uom_code}` : ""}
                    </td>
                    <td className="px-2 py-2 text-right tabular-nums font-medium">{rem}</td>
                    <td className="px-2 py-2 text-right tabular-nums">
                      {stagedLine ? (
                        <span>
                          {stagedLine.weight}
                          {stagedLine.width ? (
                            <span className="block text-[10px] text-muted-foreground">
                              W {stagedLine.width}
                              {stagedLine.wt_per_m ? ` · ${stagedLine.wt_per_m} wt/mm` : ""}
                            </span>
                          ) : null}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="px-2 py-2 text-right">
                      <Button
                        type="button"
                        size="xs"
                        variant={stagedLine ? "outline" : "default"}
                        onClick={() => setEditingLineId(line.id)}
                      >
                        <Pencil className="size-3" />
                        {stagedLine ? "Edit" : "Receive"}
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <CollapsibleBlock title="Receipt notes">
          <FieldGrid>
            <Field label="Notes" htmlFor="grn-notes" span={12}>
              <Textarea
                id="grn-notes"
                rows={2}
                className="text-xs"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Optional GRN notes"
              />
            </Field>
          </FieldGrid>
        </CollapsibleBlock>
      </div>

      <DocumentStickyBar>
        <div className="flex flex-wrap items-center gap-3 text-xs">
          <span>
            Staged <strong className="tabular-nums">{staged.length}</strong> / {openLines.length}
          </span>
          <span>
            Receipt wt <strong className="tabular-nums">{totalWeight.toFixed(3)}</strong>
          </span>
        </div>
        <Button
          type="button"
          size="sm"
          disabled={receive.isPending || !staged.length}
          onClick={() => void postReceipt()}
        >
          {receive.isPending ? <Loader2 className="animate-spin" /> : <PackageCheck />}
          Post receipt
        </Button>
      </DocumentStickyBar>

      <ReceiveLineDialog
        open={Boolean(editingLine)}
        line={editingLine}
        locationOptions={locationOptions}
        staged={staged.find((s) => s.purchase_order_line_id === editingLineId) ?? null}
        defaults={{
          location_id: bulkLocationId,
          material_grade_id: editingLine?.material_grade_id || "",
          shape_size_id: editingLine?.shape_size_id || "",
          quantity: editingLine?.qty ? String(editingLine.qty) : "1",
          weight: editingLine ? String(remainingWeight(editingLine)) : "",
        }}
        busy={receive.isPending}
        onClose={() => setEditingLineId(null)}
        onConfirm={(input) => {
          setStaged((prev) => {
            const without = prev.filter((s) => s.purchase_order_line_id !== input.purchase_order_line_id);
            return [...without, input];
          });
          setEditingLineId(null);
          toast.success(`Line staged`);
        }}
      />

      <ConfirmDialog
        open={Boolean(partialPrompt)}
        onOpenChange={(open) => {
          if (!open) {
            setPartialPrompt(null);
            goToPo();
          }
        }}
        title="Close this PO or wait for pending?"
        description={
          partialPrompt
            ? `${partialPrompt.receipt_no} posted, but some ordered quantity is still pending. Close the PO now, or keep it open (Partial) until the rest arrives.`
            : undefined
        }
        confirmLabel="Close PO now"
        cancelLabel="Wait for pending"
        loading={updateStatus.isPending}
        onConfirm={async () => {
          await updateStatus.mutateAsync({ id: poId, status: "closed" });
          setPartialPrompt(null);
          goToPo();
        }}
      />
    </DocumentPage>
  );
}
